import { useMemo, type ReactNode } from 'react';
import { Subtitle1 } from '@fluentui/react-components';
import { ItemsTable, PendingApprovalsTable, type TItem } from '@modules/items';
import { TabbedContent, type TTabConfig } from '@shared/components/TabbedContent';
import OpenDashboardButton from '@modules/dashboard/components/OpenDashboardButton';

type TProps = {
  recentItems: TItem[];
  pendingApprovals: TItem[];
  dashboardHref?: string;
  trainingContent?: ReactNode;
};

type TActivityTab = 'pending' | 'training' | 'recent';

/**
 * Renders the "Activity" section with tabbed views for recently opened items and pending approvals.
 */
const ActivitySection = ({ recentItems, pendingApprovals, dashboardHref, trainingContent }: TProps) => {
  const tabs: TTabConfig<TActivityTab>[] = useMemo(() => {
    const activityTabs: TTabConfig<TActivityTab>[] = [
      {
        id: 'pending',
        label: 'Pending signatures',
        count: pendingApprovals.length,
        showCount: true,
        disabledTooltip: 'No pending signatures.',
        content: <PendingApprovalsTable items={pendingApprovals} />,
      },
    ];

    if (trainingContent) {
      activityTabs.push({
        id: 'training',
        label: 'My training',
        count: 1,
        showCount: true,
        autoSelect: false,
        content: trainingContent,
      });
    }

    activityTabs.push({
      id: 'recent',
      label: 'Recently opened',
      count: recentItems.length,
      disabledTooltip: 'No recently opened items.',
      content:
        recentItems.length > 0 ? (
          <ItemsTable dateType="viewed" rows={recentItems.map((item) => ({ key: item._id, item }))} />
        ) : (
          <p>No recently opened items.</p>
        ),
    });

    return activityTabs;
  }, [pendingApprovals, recentItems, trainingContent]);

  return (
    <div className="flex flex-col space-y-4">
      <Subtitle1>Activity</Subtitle1>
      <TabbedContent
        tabs={tabs}
        defaultTab="recent"
        autoSelectFirstNonEmpty
        action={dashboardHref ? <OpenDashboardButton href={dashboardHref} /> : undefined}
      />
    </div>
  );
};

export default ActivitySection;
